import { useState, type MouseEvent } from "react";

function shortId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}

/**
 * FIX-008: the full UUID stays available as SECONDARY technical metadata
 * only — never the primary label — behind this explicit "Copy ID"
 * action, so a customer never has to read/transcribe a raw UUID by eye.
 */
export function CopyIdButton({ id }: { readonly id: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy(event: MouseEvent): Promise<void> {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — the full id
      // remains readable via this button's own title attribute.
    }
  }

  return (
    <button type="button" className="artifact-copy-id" onClick={(event) => void handleCopy(event)} title={id}>
      {copied ? "Copied" : "Copy ID"}
    </button>
  );
}

/**
 * FIX-008 (thumbnail-first artifact identification): the primary
 * representation of a Gate 1/Gate 2 artifact is a small VISUAL thumbnail,
 * not its internal UUID — the UUID moves to a short/secondary form plus
 * an explicit "Copy ID" action. `thumbnailSrc` MUST already be an
 * authorized-endpoint URL (e.g. `/videos/:tenantId/persona-source-stills/:id/preview`,
 * `/videos/:tenantId/:videoId/final`) — this component never accepts a
 * public/signed media URL, so it can never widen the FIX-002/FIX-006
 * tenant authorization boundary; when no such URL is available it renders
 * a neutral placeholder instead of fetching or fabricating one.
 *
 * `mediaType`: `"image"` (default) renders `thumbnailSrc` as a still
 * image — the correct, and only, representation for a Gate 1 artifact
 * (which IS a still). `"video"` renders it as a muted, `preload="metadata"`
 * `<video>` element instead — the browser natively decodes and displays
 * the video's own real first frame, no server-side thumbnail generation
 * needed. Callers must use `"video"` whenever `thumbnailSrc` points at an
 * actual video file (e.g. History's video list) — pointing an `<img>` at
 * a video URL would silently render nothing; conversely, using an
 * unrelated Gate 1 still as a stand-in for a VIDEO's own thumbnail is
 * exactly the "unrelated still" this mode exists to avoid.
 */
export function ArtifactIdentity({
  id,
  thumbnailSrc,
  label,
  mediaType = "image",
}: {
  readonly id: string;
  readonly thumbnailSrc: string | null;
  readonly label: string;
  readonly mediaType?: "image" | "video";
}) {
  function openPreview(event: MouseEvent): void {
    event.stopPropagation();
    if (thumbnailSrc !== null) {
      window.open(thumbnailSrc, "_blank", "noopener,noreferrer");
    }
  }

  return (
    <div className="artifact-identity">
      {thumbnailSrc !== null ? (
        mediaType === "video" ? (
          <video className="artifact-thumb" src={thumbnailSrc} preload="metadata" muted playsInline onClick={openPreview} />
        ) : (
          <img className="artifact-thumb" src={thumbnailSrc} alt="" loading="lazy" onClick={openPreview} />
        )
      ) : (
        <div className="artifact-thumb-placeholder" aria-hidden="true">
          ▶
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        <div>{label}</div>
        <div className="artifact-id-short">
          <code>{shortId(id)}</code> <CopyIdButton id={id} />
        </div>
      </div>
    </div>
  );
}
